import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Crown, Globe, WhatsappLogo } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITION_KEYS = ['worldCup.winner', 'worldCup.runnerUp', 'worldCup.thirdPlace', 'worldCup.fourthPlace'];
const POSITION_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32', '#6B7280'];

export default function SharedPredictionPage() {
  const { token } = useParams();
  const { i18n, t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/wc/share/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError('Prediction not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0A0A0A',
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      const link = document.createElement('a');
      link.download = `wc2026-prediction-${data?.username || 'card'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-accent text-xl mb-4">{t('share.notFound')}</p>
          <Link to="/" className="text-primary hover:underline">{t('share.goHome')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center py-8 px-4" data-testid="shared-prediction-page">
      {/* Downloadable Card */}
      <div
        ref={cardRef}
        className="w-full max-w-lg bg-[#0A0A0A] border-2 border-[#FFD700]/40 rounded-sm overflow-hidden"
        data-testid="share-card"
      >
        {/* Header */}
        <div className="bg-black p-5 flex items-center justify-between border-b border-[#FFD700]/30">
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 bg-black border-2 border-[#FFD700] rounded-sm flex items-center justify-center">
              <Crown size={18} weight="fill" className="text-[#FFD700] absolute -top-1" />
              <span className="font-heading text-xl font-black text-[#FFD700] mt-2">10</span>
            </div>
            <div>
              <span className="font-heading text-lg font-black tracking-tighter text-white block leading-none">The Kings Head</span>
              <span className="font-heading text-xs font-bold tracking-wider text-[#FFD700] leading-none">PREDICTIONS</span>
            </div>
          </div>
          <Globe size={24} className="text-[#FFD700]" />
        </div>

        {/* Title */}
        <div className="px-5 pt-4 pb-2">
          <p className="text-text-muted text-xs uppercase tracking-widest">World Cup 2026</p>
          <h2 className="font-heading text-2xl font-black text-white tracking-tight">
            {data.username}'s {t('share.prediction')}
          </h2>
        </div>

        {/* Positions */}
        <div className="px-5 py-3 space-y-2">
          {data.teams.map((entry, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-sm"
              style={{ backgroundColor: `${POSITION_COLORS[i]}10`, borderLeft: `3px solid ${POSITION_COLORS[i]}` }}
            >
              <span className="text-xs uppercase tracking-wide font-bold w-20 flex-shrink-0" style={{ color: POSITION_COLORS[i] }}>
                {t(POSITION_KEYS[i])}
              </span>
              {entry.team?.flag && (
                <img src={entry.team.flag} alt="" className="w-8 h-5 object-cover rounded-sm flex-shrink-0" />
              )}
              <span className="text-white font-medium text-base">{entry.team?.name || 'Unknown'}</span>
            </div>
          ))}
        </div>

        {/* Total Goals */}
        <div className="px-5 py-3 border-t border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-text-muted text-xs uppercase tracking-wide">{t('share.totalGoals')}</span>
            <span className="font-mono text-2xl font-bold text-primary">{data.total_goals}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-black/50 border-t border-white/10 flex items-center justify-between">
          <span className="text-text-muted text-xs">thekingsheadpredictions.com</span>
          <span className="text-[#FFD700] text-xs font-bold">WC 2026</span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full max-w-lg">
        <button
          onClick={handleDownload}
          data-testid="download-card-btn"
          className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all text-center"
        >
          Download Card
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`Check out ${data?.username}'s World Cup 2026 prediction! Can you beat it? 🏆⚽\n${window.location.href}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-share-btn"
          className="flex-1 py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold uppercase tracking-wide rounded-sm transition-all text-center flex items-center justify-center gap-2"
        >
          <WhatsappLogo size={20} weight="bold" />
          {t('share.shareWhatsapp')}
        </a>
        <Link
          to="/"
          data-testid="make-prediction-link"
          className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide rounded-sm transition-all text-center border border-white/20"
        >
          {t('share.makePrediction')}
        </Link>
      </div>
    </div>
  );
}
